import React, { useContext } from "react";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { Link, Icon, StatusIcon } from "components/common";
import Routes from "routing/Routes";
import { Input } from "components/bootstrap";
import FormDataContext from "integrations/contexts/FormDataContext";
import { ApiContext } from "integrations/aws/context/Api";
import useFetch from "integrations/hooks/useFetch";
import FormWrap from "integrations/aws/common/FormWrap";
import { ApiRoutes } from "integrations/aws/common/Routes";
import { DEFAULT_KINESIS_LOG_TYPE, KINESIS_LOG_TYPES } from "integrations/aws/common/constants";
import { toAWSRequest } from "integrations/aws/common/formDataAdapter";
import Store from "logic/local-storage/Store";
const Container = styled.div`
  border: 1px solid #a6afbd;
  margin: 25px 0;
  padding: 15px;
  border-radius: 4px;
`;
const Subheader = styled.h3`
  margin: 0 0 10px;
`;
const ReviewItems = styled.ul`
  list-style: none;
  margin: 0 0 25px 10px;
  padding: 0;

  li {
    padding: 5px;

    &:nth-of-type(odd) {
      background-color: rgb(220 225 229 / 40%);
    }
  }

  strong::after {
    content: ':';
    margin-right: 5px;
  }
`;
const EditAnchor = styled.a`
  font-size: 12px;
  margin-left: 5px;
  font-style: italic;
  cursor: pointer;

  &::before {
    content: '(';
  }

  &::after {
    content: ')';
  }
`;
const ArnErrorMessage = styled.span`
  color: #856404;
  background-color: #fff3cd;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
`;
const Default = ({ value }) => {
  const { t } = useTranslation("integrations");
  return /* @__PURE__ */ React.createElement(React.Fragment, null, value, " ", /* @__PURE__ */ React.createElement("small", null, t("aws_cloudwatch_step_review.default_label")));
};
const StepReview = ({ onSubmit, onEditClick, externalInputSubmit = false }) => {
  var _a, _b, _c, _d;
  const { t } = useTranslation("integrations");
  const { formData } = useContext(FormDataContext);
  const { logData } = useContext(ApiContext);
  const {
    awsAuthenticationType,
    awsCloudWatchAddFlowLogPrefix = { value: void 0 },
    awsCloudWatchStoreFullMessage = { value: void 0 },
    awsAssumeRoleARN = { value: void 0 },
    awsExternalId = { value: void 0 },
    awsAccessKey = { value: void 0 },
    awsCloudWatchAwsRegion,
    awsCloudWatchBatchSize,
    awsEndpointCloudWatch = { value: void 0 },
    awsCloudWatchKinesisInputType = { value: DEFAULT_KINESIS_LOG_TYPE },
    awsCloudWatchKinesisStream,
    awsCloudWatchName,
    awsCloudWatchThrottleEnabled = { value: void 0 },
    awsEndpointDynamoDB = { value: void 0 },
    awsEndpointIAM = { value: void 0 },
    awsEndpointKinesis = { value: void 0 },
    overrideSource = { value: void 0 }
  } = formData;
  const throttleEnabled = !!awsCloudWatchThrottleEnabled.value;
  const addPrefix = !!awsCloudWatchAddFlowLogPrefix.value;
  const storeFullMessage = !!awsCloudWatchStoreFullMessage.value;
  const awsCloudwatchKinesisStreamArn = (_b = (_a = formData.awsCloudwatchKinesisStreamArn) == null ? void 0 : _a.value) != null ? _b : "";
  const [fetchSubmitStatus, setSubmitFetch] = useFetch(
    null,
    (result) => {
      if (result == null ? void 0 : result.id) {
        Store.sessionSet("setup_wizard_input_id", result.id);
      }
      onSubmit();
    },
    "POST",
    toAWSRequest(formData, {
      name: awsCloudWatchName.value,
      region: awsCloudWatchAwsRegion.value,
      aws_input_type: awsCloudWatchKinesisInputType.value,
      stream_name: awsCloudWatchKinesisStream.value,
      batch_size: Number(awsCloudWatchBatchSize.value || awsCloudWatchBatchSize.defaultValue),
      enable_throttling: throttleEnabled,
      add_flow_log_prefix: addPrefix,
      store_full_message: storeFullMessage,
      kinesis_stream_arn: awsCloudwatchKinesisStreamArn,
      aws_external_id: (_c = awsExternalId.value) != null ? _c : "",
      override_source: (_d = overrideSource == null ? void 0 : overrideSource.value) != null ? _d : ""
    })
  );
  const formError = fetchSubmitStatus.error ? {
    full_message: fetchSubmitStatus.error,
    nice_message: /* @__PURE__ */ React.createElement("span", null, t("aws_cloudwatch_step_review.save_error"))
  } : null;
  const handleSubmit = () => {
    if (externalInputSubmit) {
      onSubmit(formData);
      return;
    }
    setSubmitFetch(ApiRoutes.INTEGRATIONS.AWS.KINESIS.SAVE);
  };
  return /* @__PURE__ */ React.createElement(
    FormWrap,
    {
      onSubmit: handleSubmit,
      buttonContent: t("aws_cloudwatch_step_review.complete_button"),
      loading: fetchSubmitStatus.loading,
      error: formError,
      title: t("aws_cloudwatch_step_review.title"),
      description: t("aws_cloudwatch_step_review.description")
    },
    /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(Subheader, null, t("aws_cloudwatch_step_review.setting_up_cloudwatch"), " ", /* @__PURE__ */ React.createElement("small", null, /* @__PURE__ */ React.createElement(EditAnchor, { onClick: onEditClick("authorize") }, t("aws_cloudwatch_step_review.edit")))), /* @__PURE__ */ React.createElement(ReviewItems, null, /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_name")), /* @__PURE__ */ React.createElement("span", null, awsCloudWatchName.value)), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_authorization_type")), /* @__PURE__ */ React.createElement("span", null, awsAuthenticationType.value)), awsAccessKey.value && /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_aws_key")), /* @__PURE__ */ React.createElement("span", null, awsAccessKey.value)), awsAssumeRoleARN.value && /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_assumed_role_arn")), /* @__PURE__ */ React.createElement("span", null, awsAssumeRoleARN.value)), awsExternalId.value && /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_external_id")), /* @__PURE__ */ React.createElement("span", null, awsExternalId.value)), awsEndpointCloudWatch.value && /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_cloudwatch_endpoint")), /* @__PURE__ */ React.createElement("span", null, awsEndpointCloudWatch.value)), awsEndpointDynamoDB.value && /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_dynamodb_endpoint")), /* @__PURE__ */ React.createElement("span", null, awsEndpointDynamoDB.value)), awsEndpointIAM.value && /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_iam_endpoint")), /* @__PURE__ */ React.createElement("span", null, awsEndpointIAM.value)), awsEndpointKinesis.value && /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_kinesis_endpoint")), /* @__PURE__ */ React.createElement("span", null, awsEndpointKinesis.value)), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_aws_region")), /* @__PURE__ */ React.createElement("span", null, awsCloudWatchAwsRegion.value))), /* @__PURE__ */ React.createElement(Subheader, null, t("aws_cloudwatch_step_review.setting_up_kinesis"), " ", /* @__PURE__ */ React.createElement("small", null, /* @__PURE__ */ React.createElement(EditAnchor, { onClick: onEditClick("kinesis-setup") }, t("aws_cloudwatch_step_review.edit")))), /* @__PURE__ */ React.createElement(ReviewItems, null, /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_kinesis_stream")), /* @__PURE__ */ React.createElement("span", null, awsCloudWatchKinesisStream.value)), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_kinesis_stream_arn")), /* @__PURE__ */ React.createElement("span", null, !awsCloudwatchKinesisStreamArn ? /* @__PURE__ */ React.createElement(ArnErrorMessage, null, t("aws_cloudwatch_step_review.stream_arn_error")) : awsCloudwatchKinesisStreamArn)), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_global_input")), /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(Icon, { name: "check" }))), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_record_batch_size")), /* @__PURE__ */ React.createElement("span", null, awsCloudWatchBatchSize.value ? awsCloudWatchBatchSize.value : /* @__PURE__ */ React.createElement(Default, { value: awsCloudWatchBatchSize.defaultValue }))), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_enable_throttling")), /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(StatusIcon, { active: throttleEnabled }))), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_flow_log_prefix")), /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(StatusIcon, { active: addPrefix }))), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_store_full_message")), /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(StatusIcon, { active: storeFullMessage }))), overrideSource.value && /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_override_source")), /* @__PURE__ */ React.createElement("span", null, overrideSource.value))), /* @__PURE__ */ React.createElement(Subheader, null, t("aws_cloudwatch_step_review.formatting")), /* @__PURE__ */ React.createElement(ReviewItems, null, /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement("strong", null, t("aws_cloudwatch_step_review.label_log_type")), /* @__PURE__ */ React.createElement("span", null, KINESIS_LOG_TYPES.find((type) => type.value === awsCloudWatchKinesisInputType.value).label))), /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "aws_cloudwatch_step_review.pipeline_hint",
        components: { pipelineLink: /* @__PURE__ */ React.createElement(Link, { to: Routes.SYSTEM.PIPELINES.RULES }) }
      }
    )), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "awsCloudWatchLog",
        type: "textarea",
        label: "",
        value: logData && logData.message || t("aws_cloudwatch_step_review.no_response"),
        rows: 10,
        disabled: true
      }
    ))
  );
};
export default StepReview;
